/**
 * environment/ — detailed realistic procedural environment (PCG) for Latium.
 *
 * Recreates the Unreal L_Skirmish_Latium_5km landscape direction in three.js:
 * Mediterranean, restrained olive/earth palette, dense realistic vegetation.
 * Canonical references (read-only, Unreal repo):
 *   - biome species/weights/heights: data/biomes.json (Italy set)
 *   - palette + layer vocabulary: landscape live checkpoint (V013 tints,
 *     near meadow (0.25,0.34,0.18) / near dry (0.38,0.31,0.18))
 *   - map bounds: 5 km x 5 km, height range -7.5..128 m
 *
 * ---------------------------------------------------------------------------
 * INTEGRATION POINT (for src/main.ts — the one-line hookup):
 *
 *   import { initEnvironment } from "./render/environment/index.js";
 *
 *   // replaces the placeholder terrain in createWorld():
 *   const env = initEnvironment(scene, { seed: "latium-skirmish-01" });
 *   const nav = new NavSystem({ heightAt: env.heightAt }); // sim stays in sync
 *
 *   // per frame (after camera update):
 *   env.update(camera, elapsedSeconds);
 *
 *   // optional: env.setTimeOfDay(0.5); env.dispose();
 * ---------------------------------------------------------------------------
 *
 * Asset note: vegetation stays procedural low-poly geometry tuned for RTS
 * camera distance (the vendored Unreal world sets cover ground/grass systems).
 * If denser foliage is wanted later, swap the SpeciesDef.build()
 * implementations in scatter.ts for GLB-derived geometry; placement and
 * palettes stay valid.
 */
import * as THREE from "three";
import { Heightfield, createLatium } from "../../sim/pcg";
import { Terrain } from "./terrain";
import { Water } from "./water";
import { createSky, type SkyRig, type TimeOfDay } from "./sky";
import { buildScatter, type ScatterCounts } from "./scatter";

export { Heightfield, createLatium };
export type { TimeOfDay, ScatterCounts, SkyRig };

export interface EnvironmentOpts {
  /** Deterministic seed. Same seed -> identical terrain. Default "latium". */
  seed?: string;
  /** World size in meters. Default 5000 (canonical). */
  size?: number;
  /**
   * Vertical exaggeration of the relief. Default 1. Skirmish maps pass
   * ~0.3 so the heightfield fits the gameplay vertical budget.
   */
  relief?: number;
  /** Initial time of day 0..1 (0=midnight, 0.5=noon). Default 0.42 (morning). */
  timeOfDay?: number;
  /** Custom terrain material (e.g. fog-of-war patched). Default vertex-color standard. */
  terrainMaterial?: THREE.Material;
  /** Sky fog-distance multiplier for smaller maps (default size/5000). */
  fogScale?: number;
  /** Sky dome radius (default min(9000, 2.5 * fog far)). */
  domeRadius?: number;
  /** When set, the sky sun casts shadows over ±shadowExtent of map center. */
  shadowExtent?: number;
}

export interface Environment {
  /** The sim/render shared heightfield. */
  field: Heightfield;
  /** Drop-in replacement for World's groundY / NavSystem heightAt. */
  heightAt: (x: number, z: number) => number;
  group: THREE.Group;
  counts: ScatterCounts;
  /** Number of terrain chunk meshes. */
  terrainChunks: number;
  /** Number of water surface meshes (river ribbons + lake). */
  waterMeshes: number;
  /** The sky rig: sun/hemi lights, day/night presentation. */
  sky: SkyRig;
  setTimeOfDay(t: TimeOfDay): void;
  /** Call once per frame after the camera moves. */
  update(camera: THREE.Camera, elapsed: number): void;
  dispose(): void;
}

export function initEnvironment(scene: THREE.Scene, opts: EnvironmentOpts = {}): Environment {
  const size = opts.size ?? 5000;
  const fogScale = opts.fogScale ?? size / 5000;
  const field = new Heightfield(opts.seed ?? "latium", { size, relief: opts.relief });
  const group = new THREE.Group();
  group.name = "LatiumEnvironment";

  const terrain = new Terrain(field, {
    material: opts.terrainMaterial,
    receiveShadow: opts.shadowExtent !== undefined,
  });
  group.add(terrain.group);

  const water = new Water(field);
  group.add(water.group);

  const sky = createSky({
    fogScale,
    domeRadius: opts.domeRadius ?? Math.min(9000, 9500 * fogScale * 1.2),
    shadowExtent: opts.shadowExtent,
  });
  group.add(sky.group);

  const scatter = buildScatter(field);
  group.add(scatter.group);

  // Distant apron: a flat disc under the heightfield edge so the map never
  // shows a hard border against the sky at grazing angles.
  const apron = new THREE.Mesh(
    new THREE.CircleGeometry(Math.max(11000, size * 2.2), 48),
    new THREE.MeshLambertMaterial({ color: 0x6f6650 }),
  );
  apron.rotation.x = -Math.PI / 2;
  apron.position.y = -9;
  group.add(apron);

  scene.add(group);

  let tod = opts.timeOfDay ?? 0.42;
  const applyTOD = () => sky.setTimeOfDay(tod, scene);

  applyTOD();

  const heightAt = (x: number, z: number) => field.heightAt(x, z);

  return {
    field,
    heightAt,
    group,
    counts: scatter.counts,
    terrainChunks: terrain.chunkCount,
    waterMeshes: water.meshCount,
    sky,
    setTimeOfDay(t: TimeOfDay) {
      tod = t;
      applyTOD();
    },
    update(camera: THREE.Camera, elapsed: number) {
      terrain.update(camera);
      scatter.update(camera, elapsed);
      water.update(elapsed);
      // Keep the sky dome centered on the camera so the horizon never clips.
      sky.group.position.set(camera.position.x, 0, camera.position.z);
    },
    dispose() {
      scene.remove(group);
      terrain.dispose();
      water.dispose();
      sky.dispose();
      scatter.dispose();
    },
  };
}
